import React from "react"
import { Box } from "@carrotfertility/carotene-core"
import useGetThreads from "#/components/messaging/hooks/useGetThreads"
import { ProviderUnreadMessagesDot } from "#/pages/consumer/messages/ProviderUnreadMessagesDot"

export function UnreadMessagesDot({
  showProviderUnread = false
}: {
  /**
   * Whether the member's PROVIDER conversations count toward the dot. True only for a member who has
   * the Clinicians tab — see `MessagesNavItem`.
   *
   * A gate rather than a source: when it is false the provider half is never mounted, so nothing asks
   * the BFF about a member who has no Clinicians tab to read.
   */
  showProviderUnread?: boolean
} = {}): JSX.Element | null {
  const { data: messageThreads } = useGetThreads()
  const hasUnread = messageThreads?.threads?.some((thread) => thread.hasUnreadMessages)

  // CARROT FIRST, and it short-circuits, which is what guarantees ONE dot rather than two: the two
  // sources are independent, both can be unread at once, and "you have something to read" is one fact
  // however many places it came from. It also means the provider chunk is not fetched at all for a
  // member whose dot is already lit, which costs them nothing — the dot is already saying the only thing
  // it can say.
  if (hasUnread) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }} data-testid="unread-messages-dot">
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.tokens.borderRadius.round,
            // 7px is not a spacing step — the tokens either side are `xxs` (4px) and `xs` (8px) — and
            // rounding it to one would change the dot's size. The package's own dot draws 7px too
            // (packages/messages/src/components/UnreadMessagesDot.tsx); the nav renders one or the
            // other, never both, so the two have to agree exactly or the mark would resize with its
            // source.
            blockSize: "7px",
            inlineSize: "7px"
          })}
        />
      </Box>
    )
  }
  if (!showProviderUnread) {
    return null
  }

  // The provider half, which draws the same mark from inside the messages package. Reached through a
  // lazy chunk, so nothing about provider messaging is in the nav's own bundle.
  return <ProviderUnreadMessagesDot />
}
